import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import Button from 'react-bootstrap/Button';
import { FormattedMessage } from 'react-intl';
import { useTable, useSortBy, usePagination } from 'react-table';
import { useLocation } from 'react-router-dom';
import { useUserRestrictions } from '../../auth/user-restrictions';
import { useNavigate } from '../../routing';
import { useTeamInfo } from '../../team-id-context';
import { ReactComponent as UsersIcon } from '../../shared/icons/users-group-people.svg';
import { ReactComponent as TrashIcon } from '../../shared/icons/trashcan-icon.svg';
import { Table } from '../table';
import { Dropdown, DropdownToggle, DropdownBody } from '../dropdown';
import styles from './styles.module.scss';
import ContactSlideout from './ContactSlideout';
import DeleteContactModal from './DeleteContactModal';
import { CustomFieldManagerSlideout } from './CustomFieldManagerSlideout';
import ImportContactsSlideout from './ImportContactsSlideout';
import { ReactComponent as GearIcon } from '../../shared/icons/gear-icon.svg';
import { ReactComponent as UploadIcon } from '../../shared/icons/upload-icon.svg';
import { ReactComponent as FilterIcon } from '../../shared/icons/filter-icon.svg';
import { ReactComponent as PlusIcon } from '../../shared/icons/plus-icon.svg';
import SearchInput from '../search-input';
import { MobileTable } from '../mobile-layout/MobileTable';
import { MobileContactItem } from '../mobile-layout/MobileContactItem';
import { MobilePagination } from '../mobile-layout/MobilePagination';

type ContactTypeFilter = 'all' | 'person' | 'company';

const FILTER_OPTIONS: { value: ContactTypeFilter; label: string }[] = [
  { value: 'all', label: 'Show All' },
  { value: 'person', label: 'People' },
  { value: 'company', label: 'Companies' },
];

const GET_TEAM_CONTACTS = gql`
  query GetTeamContactsForList($teamId: ID!, $search: String) {
    teamContacts(teamId: $teamId, search: $search) {
      id
      name
      email
      phone
      company
      title
      address
      contactType
      customFields
      boundDocumentsCount
      createdBy {
        id
        fullName
      }
      createdAt
    }
  }
`;

const DELETE_TEAM_CONTACT = gql`
  mutation DeleteTeamContact($id: ID!) {
    deleteTeamContact(id: $id) {
      success
      message
    }
  }
`;

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  title?: string;
  address?: string;
  contactType: 'person' | 'company';
  boundDocumentsCount?: number;
  createdBy?: {
    id: string;
    fullName: string;
  };
  createdAt: string;
}

const ContactsList: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [contactTypeFilter, setContactTypeFilter] = useState<ContactTypeFilter>('all');
  const [showCreateSlideout, setShowCreateSlideout] = useState(false);
  const [showFieldManager, setShowFieldManager] = useState(false);
  const [showImportSlideout, setShowImportSlideout] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { teamId } = useTeamInfo();
  const { canCreateTemplates, restrictionsLoading } = useUserRestrictions();

  // Check for manageFields query parameter and open slideout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('manageFields') === 'true') {
      setShowFieldManager(true);
      // Clean up URL by removing the query parameter
      params.delete('manageFields');
      const newSearch = params.toString();
      const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      navigate(newPath, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const { data, loading, refetch } = useQuery(GET_TEAM_CONTACTS, {
    variables: { teamId, search: searchQuery || '' },
    fetchPolicy: 'cache-and-network',
    skip: !canCreateTemplates || !teamId,
  });

  const [deleteContact] = useMutation(DELETE_TEAM_CONTACT);

  // Stable empty array reference to prevent infinite re-renders
  const EMPTY_CONTACTS: Contact[] = useMemo(() => [], []);
  const allContacts = data?.teamContacts || EMPTY_CONTACTS;

  // Filter contacts by type (client-side for instant filtering)
  const contacts = useMemo(() => {
    if (contactTypeFilter === 'all') return allContacts;
    return allContacts.filter((contact: Contact) => contact.contactType === contactTypeFilter);
  }, [allContacts, contactTypeFilter]);

  // Get label for current filter selection
  const currentFilterLabel = FILTER_OPTIONS.find((opt) => opt.value === contactTypeFilter)?.label || 'Show All';

  const handleDeleteClick = useCallback((e: React.MouseEvent, contact: Contact) => {
    e.stopPropagation();
    setContactToDelete(contact);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    setIsDeleting(true);
    try {
      const result = await deleteContact({ variables: { id: contactToDelete.id } });
      if (result.data?.deleteTeamContact?.success) {
        refetch();
        setContactToDelete(null);
      } else {
        alert(result.data?.deleteTeamContact?.message || 'Failed to delete contact.');
      }
    } catch (error: any) {
      console.error('Failed to delete contact:', error);
      const message = error.graphQLErrors?.[0]?.message || 'Failed to delete contact.';
      alert(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = () => {
    refetch();
    setShowCreateSlideout(false);
    setSelectedContact(null);
  };

  // Table columns - memoized with stable handleDeleteClick reference
  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        width: 200,
      },
      {
        Header: 'Type',
        accessor: 'contactType',
        width: 100,
        Cell: ({ value }: { value: string }) => (
          <span style={{ textTransform: 'capitalize' }}>{value}</span>
        ),
      },
      {
        Header: 'Email',
        accessor: 'email',
        width: 220,
        Cell: ({ value }: { value: string | undefined }) => value || '—',
      },
      {
        Header: 'Phone',
        accessor: 'phone',
        width: 150,
        Cell: ({ value }: { value: string | undefined }) => value || '—',
      },
      {
        Header: 'Company',
        accessor: 'company',
        width: 180,
        Cell: ({ value }: { value: string | undefined }) => value || '—',
      },
      {
        Header: '',
        accessor: 'actions',
        disableSortBy: true,
        width: 50,
        className: styles.actionsColumn,
        Cell: ({ row }: { row: any }) => (
          <button
            className={styles.deleteIconButton}
            onClick={(e) => handleDeleteClick(e, row.original)}
            title="Delete contact"
          >
            <TrashIcon width={18} height={18} />
          </button>
        ),
      },
    ],
    [handleDeleteClick]
  );

  // Handle row click - stable callback, row.original contains the contact data
  const handleRowClick = useCallback((row: any) => {
    setSelectedContact(row.original as Contact);
  }, []);

  // Table data with stable onRowClick reference
  const tableData = useMemo(() => {
    return contacts.map((contact: Contact) => ({
      ...contact,
      onRowClick: handleRowClick,
    }));
  }, [contacts, handleRowClick]);

  const table = useTable(
    {
      data: tableData,
      columns,
      initialState: {
        sortBy: [{ id: 'name', desc: false }],
        pageSize: 50,
        pageIndex: 0,
      },
      // Prevent auto-reset on data changes (search, filter)
      autoResetSortBy: false,
      autoResetPage: false,
    } as any,
    useSortBy,
    usePagination
  );

  // Premium access check - show upgrade message for free users
  if (!restrictionsLoading && !canCreateTemplates) {
    return (
      <div className={styles.contactsContainer}>
        <div className={styles.noAccess}>
          <UsersIcon width={48} height={48} />
          <h3>
            <FormattedMessage
              id="contacts.no-access.title"
              defaultMessage="Contacts is a Premium Feature"
            />
          </h3>
          <p>
            <FormattedMessage
              id="contacts.no-access.description"
              defaultMessage="Link contacts to document placeholders for automatic field population across all your deals."
            />
          </p>
          <Button
            variant="primary"
            onClick={() => navigate('/account')}
          >
            <FormattedMessage id="cta.upgrade-now" defaultMessage="Upgrade Now" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contactsContainer}>
      {/* Desktop Toolbar */}
      <div className={`${styles.toolbar} non-mobile-flex`}>
        {/* Search bar - full width on mobile */}
        <div className={styles.searchWrapper}>
          <SearchInput
            className=""
            value={searchQuery}
            onChange={(e: any) => setSearchQuery(e.target.value)}
            placeholder="Search contacts..."
            data-testid="search-input"
          />
        </div>

        {/* Filter and buttons row */}
        <div className={styles.filterAndActions}>
          <div className={styles.filterDropdown}>
            <Dropdown>
              <Dropdown.Toggle id="contact-type-filter" as={DropdownToggle}>
                {currentFilterLabel}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <DropdownBody>
                  {FILTER_OPTIONS.map((option) => (
                    <Dropdown.Item
                      key={option.value}
                      as={Button}
                      variant="link"
                      active={contactTypeFilter === option.value}
                      onClick={() => setContactTypeFilter(option.value)}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </DropdownBody>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Import - desktop only */}
          <div className={styles.importButton}>
            <Button
              variant="link"
              className="text-primary font-weight-bold p-0"
              onClick={() => setShowImportSlideout(true)}
              data-qa="import-contacts-button"
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
            >
              <UploadIcon style={{ width: '16px', height: '16px' }} />
              Import
            </Button>
          </div>

          {/* Manage Fields - desktop only */}
          <div className={styles.manageFieldsButton}>
            <Button
              variant="link"
              className="text-primary font-weight-bold p-0"
              onClick={() => setShowFieldManager(true)}
              data-qa="manage-contact-fields-button"
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
            >
              <GearIcon style={{ width: '16px', height: '16px' }} />
              Manage Fields
            </Button>
          </div>

          <Button variant="secondary" onClick={() => setShowCreateSlideout(true)}>
            Add Contact
          </Button>
        </div>
      </div>

      {/* Mobile Toolbar */}
      <div className={`${styles.mobileToolbar} mobile-flex`}>
        <SearchInput
          iconClassName="mobileSearch__icon"
          inputClassName="mobileSearch"
          className={styles.mobileSearchWrapper}
          value={searchQuery}
          onChange={(e: any) => setSearchQuery(e.target.value)}
          placeholder="Search contacts..."
          data-testid="search-input-mobile"
        />
        <Dropdown>
          <Dropdown.Toggle id="contact-type-filter-mobile" as={Button} className="filterBtn">
            <FilterIcon width={24} height={24} />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <DropdownBody>
              {FILTER_OPTIONS.map((option) => (
                <Dropdown.Item
                  key={option.value}
                  as={Button}
                  variant="link"
                  active={contactTypeFilter === option.value}
                  onClick={() => setContactTypeFilter(option.value)}
                >
                  {option.label}
                </Dropdown.Item>
              ))}
            </DropdownBody>
          </Dropdown.Menu>
        </Dropdown>
        <Button className="addBtn" onClick={() => setShowCreateSlideout(true)} style={{ marginLeft: '0.5rem' }}>
          <PlusIcon width={24} height={24} />
        </Button>
      </div>

      {loading && contacts.length === 0 ? (
        <div className={styles.loading}>Loading contacts...</div>
      ) : tableData.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>No contacts found</h3>
          <p>
            {searchQuery || contactTypeFilter !== 'all'
              ? 'Try adjusting your search or filter'
              : 'Add your first contact to get started. Contacts can be linked to document fields.'}
          </p>
          {!searchQuery && contactTypeFilter === 'all' && (
            <Button variant="primary" onClick={() => setShowCreateSlideout(true)}>
              Add Your First Contact
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className={`${styles.tableWrapper} non-mobile`}>
            <Table striped isStickyHeader paginationBelow isStickyPagination table={table} />
          </div>
          {/* Mobile Card List */}
          <div className={styles.mobileTableContainer}>
            <div className={styles.mobileListArea}>
              <MobileTable>
                {(table as any).page?.map((row: any) => {
                  const contact = row.original as Contact;
                  return (
                    <MobileContactItem
                      key={contact.id}
                      contact={contact}
                      onClick={() => setSelectedContact(contact)}
                    />
                  );
                })}
              </MobileTable>
            </div>
            <MobilePagination
              pageIndex={(table as any).state.pageIndex}
              pageCount={(table as any).pageCount}
              canPreviousPage={(table as any).canPreviousPage}
              canNextPage={(table as any).canNextPage}
              gotoPage={(table as any).gotoPage}
              previousPage={(table as any).previousPage}
              nextPage={(table as any).nextPage}
              totalItems={tableData.length}
              pageSize={(table as any).state.pageSize}
            />
          </div>
        </>
      )}

      {/* Create New Contact Slideout */}
      {showCreateSlideout && <ContactSlideout onClose={() => setShowCreateSlideout(false)} onSave={handleSave} />}

      {/* Edit Contact Slideout */}
      {selectedContact && (
        <ContactSlideout
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onSave={handleSave}
        />
      )}

      {/* Delete Contact Confirmation Modal */}
      <DeleteContactModal
        contact={contactToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setContactToDelete(null)}
        isDeleting={isDeleting}
      />

      {/* Custom Field Manager Slideout */}
      <CustomFieldManagerSlideout
        isOpen={showFieldManager}
        onClose={() => setShowFieldManager(false)}
        teamId={teamId || ''}
      />

      {/* Import Contacts Slideout */}
      {showImportSlideout && (
        <ImportContactsSlideout
          onClose={() => setShowImportSlideout(false)}
          onImportComplete={async () => {
            await refetch();
          }}
        />
      )}
    </div>
  );
};

export default ContactsList;
